import { useContext } from 'react'
import { observer } from 'mobx-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import c from 'classnames'

import SVG from 'presentation/SVG'
import { CustomerSelfManagementStoreContext } from 'providers/PublicStoreProvider'
import { CUSTOMER_SELF_MANAGEMENT } from 'routing/routes'
import ssnLogo from 'assets/png/Footer/ssnArgentina.png'

import styles from './publicFooter.module.scss'

const PASAP_PLATFORM = 'pasap'
const SSN_URL = 'http://www.argentina.gob.ar/ssn'
const SSN_DISPLAY_URL = SSN_URL.replace(/^https?:\/\//i, '')

const SOCIAL_LABELS = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    youtube: 'YouTube',
}

const PublicFooter = () => {
    const { t } = useTranslation('customerSelfManagement')
    const customerSelfManagementStore = useContext(CustomerSelfManagementStoreContext)
    const { platform, logo, links, footer, contactInfo } = customerSelfManagementStore
    const platformLink = platform
        ? `${CUSTOMER_SELF_MANAGEMENT}/${platform}`
        : `${CUSTOMER_SELF_MANAGEMENT}/pasap`

    const pasapWhiteLogo = platform === PASAP_PLATFORM ? footer?.logo : null
    const footerLogo = pasapWhiteLogo || logo
    const invertLogo = Boolean(footerLogo) && !pasapWhiteLogo

    return (
        <footer className={styles.footer}>
            <div className={styles.inner}>
                <div className={styles.top}>
                    {footerLogo && (
                        <Link to={platformLink} className={styles.logoLink} title={t('footerHome')}>
                            <img
                                src={footerLogo}
                                alt={t('footerHome')}
                                className={c(styles.logo, invertLogo && styles.logoInverted)}
                            />
                        </Link>
                    )}

                    <p className={styles.brandCopy}>{t('footerBrandCopy')}</p>

                    {links?.length > 0 && (
                        <nav className={styles.community} aria-label={t('footerCommunity')}>
                            <h3 className={styles.communityTitle}>{t('footerCommunity')}</h3>
                            <ul className={styles.socials}>
                                {links.map((link) => (
                                    <li key={link?.id}>
                                        <a
                                            href={link?.value}
                                            className={styles.socialLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <span className={styles.socialIcon}>
                                                <SVG tag={link?.id} type="link" />
                                            </span>
                                            {SOCIAL_LABELS[link?.id] || link?.id}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}
                </div>

                <div className={styles.divider} aria-hidden="true" />

                <div className={styles.legal}>
                    <div className={styles.legalLeft}>
                        {contactInfo && (
                            <a href={`mailto:${contactInfo}`} className={styles.legalMail}>
                                {contactInfo}
                            </a>
                        )}
                        {footer?.text && <p>{footer.text}</p>}
                    </div>

                    <div className={styles.legalRight}>
                        <a
                            href={SSN_URL}
                            className={styles.ssnLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={t('footerSsn')}
                        >
                            <span className={styles.ssnPanel}>
                                <img
                                    src={ssnLogo}
                                    alt={t('footerSsn')}
                                    className={styles.ssnLogo}
                                />
                            </span>
                            <span className={styles.ssnUrl}>{SSN_DISPLAY_URL}</span>
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default observer(PublicFooter)
